"use client"

import { useState } from "react"
import axios from "axios"

export default function CrmOnboarding() {
  const API_BASE = process.env.REACT_APP_API_BASE_URL || ''
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    password_confirmation: "",
    company_type: "",
    company_name: "",
    sub_domain: "",
    company_size: "",
    companies_use: [],
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
   const [success, setSuccess] = useState(false)

  const handleNext = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1)
    } else {
      handleSubmit()
    }
  }

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1)
  }

  const handleCompanyTypeSelect = (type) => setFormData({ ...formData, company_type: type })
  const handleCompanySizeSelect = (size) => setFormData({ ...formData, company_size: size })
  const handleFeatureToggle = (feature) => {
    const updated = formData.companies_use.includes(feature)
      ? formData.companies_use.filter((f) => f !== feature)
      : [...formData.companies_use, feature]
    setFormData({ ...formData, companies_use: updated })
  }

  const handleSubmit = async () => {
    setLoading(true)
    setError(null)
    try {
      await axios.post(
        "https://app.zeedone.com/onboarding-registration",
        {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          password_confirmation: formData.password_confirmation,
          comapny_type: formData.company_type,
          company_name: formData.company_name,
          sub_domain: formData.sub_domain,
          company_size: formData.company_size,
          companies_use: formData.companies_use,
          g_recaptcha: null,
        }
      )
      alert("Onboarding successful!")
    } catch (err) {
      console.error(err)
      setError("Submission failed. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const renderStep1 = () => (
    <div className="max-w-4xl mx-auto p-8 text-center">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-4">
          <span className="text-blue-600">{'"Welcome to Zeedone CRM! '}</span>
          <span className="text-teal-500">{'Manage your customers and grow your business easily."'}</span>
        </h1>
        <p className="text-gray-600">{'"Track leads, manage customer interactions, and monitor sales."'}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto mb-8">
        {[
          "Contact management",
          "Marketing Activities",
          "Sale Pipeline",
          "Lead management",
          "Customer Project",
          "Team Task & Projects",
          "Others",
        ].map((feature) => (
          <div key={feature} className="flex items-center space-x-2 p-4 border rounded-lg bg-white">
            <input
              type="checkbox"
              id={feature}
              checked={formData.companies_use.includes(feature)}
              onChange={() => handleFeatureToggle(feature)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor={feature} className="text-sm font-medium cursor-pointer">
              {feature}
            </label>
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleNext}
          className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-md font-medium text-lg transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  )

  const renderStep2 = () => (
    <div className="max-w-2xl mx-auto p-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-4">
          <span className="text-blue-600">{"\"Let's Set Up "}</span>
          <span className="text-teal-500">{'Your Profile!"'}</span>
        </h1>
        <p className="text-gray-600">
          Help us personalize your experience by completing your profile. This will help you get the most out of your
          CRM system.
        </p>
      </div>

      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-4">Contact details</h2>
          <p className="text-gray-600 mb-6">Please fill in your details below.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Name
            </label>
            <div className="relative">
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <div className="relative">
              <input
                id="email"
                type="email"
                placeholder="Email address"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
              Phone Number
            </label>
            <div className="relative">
              <input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="password_confirmation" className="block text-sm font-medium text-gray-700">
              Confirm Password
            </label>
            <div className="relative">
              <input
                id="password_confirmation"
                type="password"
                placeholder="••••••••"
                value={formData.password_confirmation}
                onChange={(e) => setFormData({ ...formData, password_confirmation: e.target.value })}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-6">
          <button
            onClick={handleNext}
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-2 rounded-md font-medium transition-colors"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  )

  const renderStep3 = () => (
    <div className="max-w-4xl mx-auto p-8 text-center">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-4">
          <span className="text-blue-600">Which Best Describes </span>
          <span className="text-teal-500">Your Company?</span>
        </h1>
        <p className="text-gray-600">This Helps Us Customize Works For Your Needs</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto mb-8">
        {["Industry", "IT Solution", "Finance", "Sales", "Construction", "Wholesale"].map((type) => (
          <div
            key={type}
            className={`cursor-pointer transition-all hover:shadow-md p-6 border rounded-lg ${
              formData.company_type === type ? "ring-2 ring-blue-500 bg-blue-50" : "bg-white"
            }`}
            onClick={() => handleCompanyTypeSelect(type)}
          >
            <div className="text-center font-medium">{type}</div>
          </div>
        ))}
      </div>

      <div className="flex justify-between">
        <button
          onClick={handleBack}
          className="border border-gray-300 text-gray-700 px-6 py-2 rounded-md font-medium hover:bg-gray-50 transition-colors"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md font-medium transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  )

  const renderStep4 = () => (
    <div className="max-w-2xl mx-auto p-8 text-center">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-4">
          <span className="text-blue-600">What is Your </span>
          <span className="text-teal-500">company's Name?</span>
        </h1>
        <p className="text-gray-600">Please Provide Your Company Name Below</p>
      </div>

      <div className="space-y-6 max-w-md mx-auto">
        <div className="space-y-2">
          <label htmlFor="company_name" className="text-left block font-medium text-gray-700">
            Company Name
          </label>
          <input
            id="company_name"
            type="text"
            placeholder="Enter your company name"
            value={formData.company_name}
            onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="sub_domain" className="text-left block font-medium text-gray-700">
            Sub domain
          </label>
          <div className="flex">
            <input
              id="sub_domain"
              type="text"
              placeholder="Enter your subdomain"
              value={formData.sub_domain}
              onChange={(e) => setFormData({ ...formData, sub_domain: e.target.value })}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
            <div className="bg-blue-600 text-white px-4 py-2 rounded-r-md flex items-center font-medium">
              .zeedone.com
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between mt-8">
        <button
          onClick={handleBack}
          className="border border-gray-300 text-gray-700 px-6 py-2 rounded-md font-medium hover:bg-gray-50 transition-colors"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md font-medium transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  )

  const renderStep5 = () => (
    <div className="max-w-4xl mx-auto p-8 text-center">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-4">
          <span className="text-blue-600">How Many People Work In </span>
          <span className="text-teal-500">Your Company?</span>
        </h1>
        <p className="text-gray-600">This Helps us Suggest Suitable Subscription Plans.</p>
      </div>

      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4 text-left">{"What's your Company Size?"}</h3>
        <p className="text-gray-600 mb-6 text-left">Please select the Number of Employees in your Company.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto mb-8">
        {["1-10 People", "10-100 People", "100-200 People", "More than 200 People"].map((size) => (
          <div
            key={size}
            className={`cursor-pointer transition-all hover:shadow-md p-6 border rounded-lg ${
              formData.company_size === size ? "ring-2 ring-blue-500 bg-blue-50" : "bg-white"
            }`}
            onClick={() => handleCompanySizeSelect(size)}
          >
            <div className="text-center font-medium">{size}</div>
          </div>
        ))}
      </div>

      <div className="flex justify-between">
        <button
          onClick={handleBack}
          className="border border-gray-300 text-gray-700 px-6 py-2 rounded-md font-medium hover:bg-gray-50 transition-colors"
        >
          Back
        </button>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-md font-medium text-lg transition-colors disabled:opacity-70"
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>
      </div>
      
      {error && (
        <div className="mt-4 text-red-500">
          {error}
        </div>
      )}
      
      {success && (
        <div className="mt-4 text-green-500">
          Registration successful! Redirecting...
        </div>
      )}
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      {currentStep === 1 && renderStep1()}
      {currentStep === 2 && renderStep2()}
      {currentStep === 3 && renderStep3()}
      {currentStep === 4 && renderStep4()}
      {currentStep === 5 && renderStep5()}
    </div>
  )
}